import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Input,
  ViewChild,
  afterNextRender,
  inject,
  signal,
  type WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ModalService } from "@core/services/modal-service/modal.service";
import { TermsConditions } from "@core/models/terms-conditions.model";
import { TermsConditionsPositionEnum } from "@core/enums/terms-conditions-position.enum";
import { TranslateModule } from "@ngx-translate/core";
import { SanitizeHtmlPipe } from "@shared/pipe/sanitize-html.pipe";
import { ActionOptionsEnum } from "@core/enums/action-options.enum";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";

import { ButtonDirective } from "@shared/directives/button.directive";
export type TermsConditionsConfirmPersistence = () => Observable<unknown>;

@Component({
  selector: "app-terms-conditions-modal",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./terms-conditions-modal.component.html",
  styleUrls: ["./terms-conditions-modal.component.scss"],
  imports: [ButtonDirective, CommonModule, TranslateModule, SanitizeHtmlPipe],
})
export class TermsConditionsModalComponent implements AfterViewInit {
  @Input() term: TermsConditions | null = null;
  @Input() confirmPersistence: TermsConditionsConfirmPersistence | null = null;

  @ViewChild("contentScrollPanel", { read: ElementRef })
  private readonly contentScrollPanel?: ElementRef<HTMLElement>;

  readonly backLabelKey: string = "termsConditions.back";

  /** Los mueve la suscripcion de `onConfirm`, no el clic que la dispara. */
  protected readonly isSubmitting: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly confirmRequestFailed: WritableSignal<boolean> = signal<boolean>(false);

  private readonly modalService: ModalService = inject(ModalService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly injector: Injector = inject(Injector);

  get showCancelButton(): boolean {
    return this.term !== null && this.term.position !== TermsConditionsPositionEnum.END;
  }

  ngAfterViewInit(): void {
    this.scheduleScrollPanelToTop();
  }

  onConfirm(): void {
    this.confirmRequestFailed.set(false);
    const persist: TermsConditionsConfirmPersistence | null = this.confirmPersistence;

    if (persist == null) {
      this.modalService.close({ action: ActionOptionsEnum.CONFIRM });
      return;
    }

    this.isSubmitting.set(true);
    persist()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize((): void => this.isSubmitting.set(false)),
      )
      .subscribe({
        next: () => {
          this.modalService.close({ action: ActionOptionsEnum.CONFIRM });
        },
        error: () => {
          this.confirmRequestFailed.set(true);
        },
      });
  }

  onCancel(): void {
    this.modalService.close({ action: ActionOptionsEnum.CANCEL });
  }

  private scheduleScrollPanelToTop(): void {
    if (!this.term) return;

    afterNextRender(
      () => {
        this.scrollContentPanelToTop();
        afterNextRender(() => this.scrollContentPanelToTop(), { injector: this.injector });
      },
      { injector: this.injector },
    );
  }

  private scrollContentPanelToTop(): void {
    const elementRef: HTMLElement | undefined = this.contentScrollPanel?.nativeElement;
    if (elementRef !== undefined) elementRef.scrollTop = 0;
  }
}
